export const SITE_URL = "https://www.durafoaminc.com";

/**
 * Build an absolute canonical URL for a given route path.
 * Use for `<link rel="canonical">` and `og:url` on every route.
 */
export function canonical(path: string = "/"): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
}

/**
 * Canonical WebSite JSON-LD node. Google reads its site-name feature (the bold
 * brand line above the URL in results) from this entity on the HOME PAGE only.
 * `publisher` points at the business record so both resolve to one entity.
 */
export function webSiteNode(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: "Durafoam Roofing",
    alternateName: ["Durafoam", "Durafoam Roofing Inc", "Durafoam Roofing LLC"],
    url: `${SITE_URL}/`,
    inLanguage: "en-US",
    publisher: { "@id": `${SITE_URL}/#business` },
  };
}

type AggregateRating = {
  ratingValue: string;
  reviewCount: string;
  bestRating?: string;
  worstRating?: string;
};

type BusinessNodeOptions = {
  /** Page-specific canonical path (defaults to site root). */
  url?: string;
  /** Optional aggregate rating block. */
  aggregateRating?: AggregateRating;
  /** Optional review array (e.g. on /reviews). */
  review?: Array<Record<string, unknown>>;
  /** Override areaServed (defaults to full service-area city list). */
  areaServed?: unknown;
};

const DEFAULT_AREA_SERVED = [
  "Phoenix", "Scottsdale", "Mesa", "Paradise Valley", "Cave Creek", "Carefree",
  "Fountain Hills", "Sun City", "Peoria", "Chandler", "Tempe", "Gilbert",
  "Glendale", "Surprise", "Anthem", "Ahwatukee", "North Phoenix",
].map((n) => ({ "@type": "City", name: n }));

/**
 * Canonical RoofingContractor JSON-LD node. Every page that emits the
 * business entity MUST use this helper so Google sees one fully-populated,
 * consistent record (telephone, address, image, priceRange, geo, etc.).
 */
export function businessNode(opts: BusinessNodeOptions = {}): Record<string, unknown> {
  const node: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": `${SITE_URL}/#business`,
    name: "Durafoam Roofing",
    alternateName: "Durafoam Roofing LLC",
    url: opts.url ? canonical(opts.url) : SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    image: `${SITE_URL}/og-image.jpg`,
    description:
      "Durafoam Roofing provides foam roofing, roof repair, roof leak detection, recoating, and free roof inspections in Phoenix. Family-owned and trusted since 1989.",
    telephone: "+1-480-941-5373",
    email: "quality@durafoaminc.com",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "2810 E Mohawk Ln",
      addressLocality: "Phoenix",
      addressRegion: "AZ",
      postalCode: "85050",
      addressCountry: "US",
    },
    geo: { "@type": "GeoCoordinates", latitude: 33.6784, longitude: -111.9817 },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "17:00",
      },
    ],
    areaServed: opts.areaServed ?? DEFAULT_AREA_SERVED,
    foundingDate: "1989",
    founder: {
      "@type": "Person",
      name: "Steve Forstie",
      jobTitle: "Founder",
    },
    member: [
      {
        "@type": "Person",
        "@id": `${SITE_URL}/tim-forstie#person`,
        name: "Tim Forstie",
        alternateName: "Timothy Forstie",
        jobTitle: "Owner",
        url: `${SITE_URL}/tim-forstie`,
        worksFor: { "@id": `${SITE_URL}/#business` },
      },
      { "@type": "Person", name: "Curtis Forstie", jobTitle: "Owner" },
    ],
    sameAs: [
      "https://www.facebook.com/durafoamroofing",
      "https://www.instagram.com/durafoamroofing",
      "https://www.linkedin.com/company/durafoam-roofing/",
      "https://www.youtube.com/@DurafoamRoofing",
      "https://rosieonthehouse.com/listing/durafoam-roofing-inc/",
    ],
    award: "Rosie on the House Certified Partner since 1996",
    memberOf: {
      "@type": "Organization",
      name: "Rosie on the House",
      url: "https://www.rosieonthehouse.com/",
    },
  };
  if (opts.aggregateRating) node.aggregateRating = { "@type": "AggregateRating", bestRating: "5", worstRating: "1", ...opts.aggregateRating };
  if (opts.review) node.review = opts.review;
  return node;
}
